import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Menu, X, ChevronDown, User } from 'lucide-react';
import { platforms } from '../mock/mock';
import { useAuth } from '../context/AuthContext';
import { PlatformLogo } from './PlatformLogo';
import SearchModal from './SearchModal';

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [platformsOpen, setPlatformsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0a0a0f]/80 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-[#ff8247] via-[#ff4d1a] to-[#ff2d6f] flex items-center justify-center overflow-hidden">
            <svg viewBox="0 0 24 24" className="w-6 h-6 text-[#0a0a0f]" fill="currentColor">
              <path d="M12 2 3 7v10l9 5 9-5V7l-9-5Zm0 2.3 6.5 3.6L12 11.5 5.5 7.9 12 4.3Zm-7 5.5 6 3.3v6.6l-6-3.3V9.8Zm14 0v6.6l-6 3.3v-6.6l6-3.3Z"/>
            </svg>
          </div>
          <div className="flex flex-col leading-none">
            <span className="text-white font-bold text-lg tracking-tight">SociBoost</span>
            <span className="text-[10px] text-[#ff8247] font-semibold uppercase tracking-widest">Trusted Worldwide</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          <Link to="/" className="text-white/80 hover:text-white transition text-sm font-medium">Accueil</Link>
          <div className="relative" onMouseEnter={() => setPlatformsOpen(true)} onMouseLeave={() => setPlatformsOpen(false)}>
            <button className="text-white/80 hover:text-white transition text-sm font-medium flex items-center gap-1">
              Réseaux <ChevronDown className="w-4 h-4" />
            </button>
            {platformsOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-[520px]">
                <div className="glass-card rounded-2xl p-4 grid grid-cols-2 gap-2">
                  {platforms.map(p => (
                    <button key={p.id} onClick={() => navigate(`/collections/${p.slug}`)}
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition text-left">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${p.color}18` }}>
                        <PlatformLogo platform={p.id} className="w-5 h-5" color={p.id === 'instagram' ? undefined : p.color} />
                      </div>
                      <div>
                        <div className="text-white text-sm font-semibold">{p.name}</div>
                        <div className="text-white/50 text-xs">{p.id === 'google' ? 'Avis · Note · Réputation' : 'Abonnés · Likes · Vues'}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <Link to="/collections" className="text-white/80 hover:text-white transition text-sm font-medium">Boutique</Link>
          <a href="#reviews" className="text-white/80 hover:text-white transition text-sm font-medium">Avis</a>
          <a href="#faq" className="text-white/80 hover:text-white transition text-sm font-medium">FAQ</a>
        </nav>

        <div className="flex items-center gap-2">
          <button onClick={() => setSearchOpen(true)} className="hidden md:flex items-center gap-2 pl-3 pr-2 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition group">
            <Search className="w-3.5 h-3.5 text-white/60 group-hover:text-white" />
            <span className="text-white/60 text-xs">Rechercher…</span>
            <kbd className="ml-1 px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-white/50 text-[9px] font-semibold">⌘K</kbd>
          </button>
          <button onClick={() => setSearchOpen(true)} className="md:hidden w-10 h-10 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 transition">
            <Search className="w-4 h-4 text-white" />
          </button>
          <Link to={user ? '/account' : '/auth/login'} data-testid="header-account-link" className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 transition" title={user ? 'Mon espace' : 'Se connecter'}>
            {user ? (
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#ff8247] to-[#ff2d6f] flex items-center justify-center font-black text-[#0a0a0f] text-xs overflow-hidden" data-testid="header-user-avatar">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  (user.name || user.email).charAt(0).toUpperCase()
                )}
              </div>
            ) : (
              <User className="w-4 h-4 text-white" />
            )}
          </Link>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full bg-white/5">
            {mobileOpen ? <X className="w-5 h-5 text-white" /> : <Menu className="w-5 h-5 text-white" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-white/5 bg-[#0a0a0f]">
          <div className="px-5 py-4 space-y-2">
            <Link to="/" onClick={() => setMobileOpen(false)} className="block py-2 text-white/80">Accueil</Link>
            <div className="grid grid-cols-2 gap-2 py-2">
              {platforms.map(p => (
                <button key={p.id} onClick={() => { navigate(`/collections/${p.slug}`); setMobileOpen(false); }}
                  className="flex items-center gap-2 p-2 rounded-lg bg-white/5">
                  <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: `${p.color}20` }}>
                    <PlatformLogo platform={p.id} className="w-4 h-4" color={p.id === 'instagram' ? undefined : p.color} />
                  </div>
                  <span className="text-white text-sm">{p.name}</span>
                </button>
              ))}
            </div>
            <Link to="/collections" onClick={() => setMobileOpen(false)} className="block py-2 text-white/80">Boutique</Link>
            <Link to={user ? '/account' : '/auth/login'} onClick={() => setMobileOpen(false)} className="block py-2 text-white/80">{user ? 'Mon compte' : 'Se connecter'}</Link>
            <a href="#reviews" onClick={() => setMobileOpen(false)} className="block py-2 text-white/80">Avis</a>
          </div>
        </div>
      )}
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
};

export default Header;
